import React, { useState, useEffect } from 'react';
import { updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import LockResetIcon from '@mui/icons-material/LockReset';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const C = {
  accent: '#2dd4bf', bg: '#f0f4f4', card: '#ffffff',
  cardBorder: 'rgba(0,0,0,0.06)', text: '#1a2e2e', muted: '#5a7a7a',
  shadow: '0 2px 10px rgba(0,0,0,0.07)', danger: '#ef4444',
};
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '16px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const PasswordChange = () => {
  const { isAuthenticated, userId, logout, token } = useAuth();

  const [oldPassword, setOldPassword]         = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading]             = useState(false);
  const [successMessage, setSuccessMessage]   = useState('');
  const [errorMessage, setErrorMessage]       = useState('');
  const [showOld, setShowOld]   = useState(false);
  const [showNew, setShowNew]   = useState(false);
  const [showConf, setShowConf] = useState(false);

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); }
  }, [isAuthenticated, token, logout]);

  const handleChangePassword = async () => {
    setErrorMessage('');
    if (!oldPassword)     { setErrorMessage('Current password is required!'); return; }
    if (!newPassword)     { setErrorMessage('New password is required!'); return; }
    if (newPassword !== confirmPassword) { setErrorMessage('Passwords do not match.'); return; }
    setIsLoading(true);
    try {
      const res = await updateData('UserTokenApi', `${userId}`, { CurrentPassword: oldPassword, NewPassword: newPassword, ConfirmPassword: confirmPassword, UserId: userId }, token);
      setSuccessMessage(res.message || 'Password changed successfully!');
      if (res.responseCode === '1111') logout();
    } catch (err) {
      console.error('Password change error:', err);
      setErrorMessage('Failed to change password. Please try again.');
    } finally { setIsLoading(false); }
  };

  const pwdField = (label, value, setter, show, setShow) => (
    <div style={{ marginBottom: '12px' }}>
      <label style={lbl}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => setter(e.target.value)}
          style={{ border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '32px', width: '100%', padding: '0 36px 0 10px', outline: 'none', backgroundColor: '#f8fafa', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
        <button onClick={() => setShow(!show)} type="button"
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex', alignItems: 'center', padding: 0 }}>
          {show ? <VisibilityOffIcon style={{ fontSize: 16 }} /> : <VisibilityIcon style={{ fontSize: 16 }} />}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px' }}>
          <LockResetIcon style={{ fontSize: 16, color: C.accent }} />
          <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Reset Password</span>
        </div>
      </div>

      {errorMessage   && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '11px', color: C.danger }}>{errorMessage}</div>}
      {successMessage && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', fontSize: '11px', color: '#16a34a' }}>{successMessage}</div>}

      {/* ── Password Form ── */}
      <div style={{ ...card, maxWidth: '420px', margin: '0 auto' }}>
        <div style={cardHead('#8b5cf6')}>🔒 Change Password</div>
        <div style={cardBody}>
          {pwdField('Current Password', oldPassword, setOldPassword, showOld, setShowOld)}
          {pwdField('New Password', newPassword, setNewPassword, showNew, setShowNew)}
          {pwdField('Confirm New Password', confirmPassword, setConfirmPassword, showConf, setShowConf)}

          <div style={{ fontSize: '10px', color: C.muted, marginBottom: '14px', lineHeight: 1.6, padding: '6px 10px', backgroundColor: '#f8fafa', borderRadius: '6px', border: `1px solid ${C.cardBorder}` }}>
            Password must be at least 8 characters and include uppercase, lowercase, and a digit.
          </div>

          <button onClick={handleChangePassword} disabled={isLoading}
            style={{ width: '100%', height: '32px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'inherit' }}>
            {isLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : '🔒 Change Password'}
          </button>
        </div>
      </div>

    </div>
  );
};

export default PasswordChange;
